import React, { useContext } from 'react';
import { UploaderProvider, useModal } from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';
import { type ApiDossiersIdActionPutActionEnum } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useDossierDocumentsCtx } from '@europrocurement/l2d-modules/providers';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import SelectedDossierContext, {
    useSelectedDossierContext,
} from '../providers/SelectedDossierContext';
import AdditionnalCollectionRequestForm from '../components/detail/AdditionnalCollectionRequestForm/AdditionnalCollectionRequestForm';
import useDossierPrescripteur from './useDossierPrescripteur';
import DossierFormValidationStatusModal from '../components/detail/DossierFormValidationStatusModal/DossierFormValidationStatusModal';
import useTransition from './useTransition';

const useAdditionnalCollectionRequest = () => {
    const { modalActions } = useModal();
    const { getDossierDocumentsByCategory } = useDossierDocumentsCtx();
    const { siblings, dossier } = useSelectedDossierContext();
    const { getPrescriber } = useDossierPrescripteur();
    const { enqueueSnackbar } = useSnackbar();
    const { canApplyTransition, applyTransition } = useTransition();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { refreshDossier } = useContext(SelectedDossierContext);

    const handleSubmitFormStatus = async (
        actionIdentifier: ApiDossiersIdActionPutActionEnum | 'save',
    ) => {
        if (actionIdentifier === 'save') {
            modalActions.close();
        } else if (canApplyTransition(dossier, actionIdentifier)) {
            applyTransition(dossier, actionIdentifier)
                .then(() => {
                    modalActions.close();
                    refreshDossier();
                })
                .catch((err) => {
                    modalActions.close();
                    apiErrorMessageSnackbar(
                        err,
                        'Une erreur est survenue lors de la modification du dossier',
                    );
                });
        }
    };

    const onSuccess = () => {
        modalActions.call(
            <DossierFormValidationStatusModal
                dossier={dossier}
                withoutSaveForm
                handleSubmit={handleSubmitFormStatus}
            />,
        );
    };

    const callAdditionnalCollectionRequestModal = async () => {
        if (!dossier.prescripteurId) {
            enqueueSnackbar('Prescripteur non trouvé', { variant: 'error' });
            return;
        }
        const prescriber = await getPrescriber(dossier.prescripteurId as number);
        if (!prescriber) {
            enqueueSnackbar('Prescripteur non trouvé', { variant: 'error' });
            return;
        }

        // Get all related dossier from collection
        modalActions.call(
            <UploaderProvider>
                <AdditionnalCollectionRequestForm
                    dossiers={siblings}
                    dossier={dossier}
                    missingDocs={getDossierDocumentsByCategory({
                        categoryId: DOC_TYPE_REQUIRED,
                    }).filter(
                        (doc) => doc.fichierId === null || typeof doc.fichierId === 'undefined',
                    )}
                    prescriber={prescriber}
                    onSuccess={onSuccess}
                />
            </UploaderProvider>,
            false,
            true,
        );
    };
    return { callAdditionnalCollectionRequestModal };
};

export default useAdditionnalCollectionRequest;
